import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { blogCategoriesQueryOptions, blogPostQueryOptions } from '@/features/blog';
import { collectionQueryOptions } from '@/features/collections';
import { pageQueryOptions } from '@/features/pages';
import { policiesQueryOptions } from '@/features/policies';
import { storeProductQueryOptions } from '@/features/store-catalog';

import { targetPath, type TargetKind } from '../schemas/target-kinds';

interface Resolved {
  label: string | null;
  detail: string | null;
  missing: boolean;
  pending: boolean;
}

// A deleted target answers 404; that must show as "missing", not throw to the route boundary.
const quiet = { throwOnError: false, retry: false } as const;

function useResolvedTarget(storeId: string, kind: TargetKind, id: string): Resolved {
  const product = useQuery({
    ...storeProductQueryOptions(storeId, id),
    ...quiet,
    enabled: kind === 'product',
  });
  const collection = useQuery({
    ...collectionQueryOptions(storeId, id),
    ...quiet,
    enabled: kind === 'collection',
  });
  const page = useQuery({ ...pageQueryOptions(storeId, id), ...quiet, enabled: kind === 'page' });
  const policies = useQuery({
    ...policiesQueryOptions(storeId),
    ...quiet,
    enabled: kind === 'policy',
  });
  const post = useQuery({
    ...blogPostQueryOptions(storeId, id),
    ...quiet,
    enabled: kind === 'blog_post',
  });
  const categories = useQuery({
    ...blogCategoriesQueryOptions(storeId),
    ...quiet,
    enabled: kind === 'blog_category',
  });

  switch (kind) {
    case 'product':
      return {
        label: product.data ? (product.data.title ?? product.data.product.title) : null,
        detail: product.data ? targetPath(kind, product.data.handle) : null,
        missing: product.isError,
        pending: product.isPending,
      };
    case 'collection':
      return {
        label: collection.data?.title ?? null,
        detail: collection.data ? targetPath(kind, collection.data.handle) : null,
        missing: collection.isError,
        pending: collection.isPending,
      };
    case 'page':
      return {
        label: page.data?.title ?? null,
        detail: page.data ? targetPath(kind, page.data.handle) : null,
        missing: page.isError,
        pending: page.isPending,
      };
    case 'policy': {
      const row = policies.data?.find((p) => p.id === id);
      return {
        label: row?.title ?? null,
        detail: row ? targetPath(kind, row.handle) : null,
        missing: policies.isError || (policies.isSuccess && !row),
        pending: policies.isPending,
      };
    }
    case 'blog_post':
      return {
        label: post.data?.title ?? null,
        detail: post.data ? targetPath(kind, post.data.handle) : null,
        missing: post.isError,
        pending: post.isPending,
      };
    case 'blog_category': {
      const row = categories.data?.find((c) => c.id === id);
      return {
        label: row?.title ?? null,
        detail: row ? targetPath(kind, row.handle) : null,
        missing: categories.isError || (categories.isSuccess && !row),
        pending: categories.isPending,
      };
    }
  }
}

/** The title of a stored target id, or "missing" when it no longer exists in the store. */
export function TargetLabel({
  storeId,
  kind,
  id,
  showPath = false,
}: {
  storeId: string;
  kind: TargetKind;
  id: string;
  showPath?: boolean;
}) {
  const { t } = useTranslation('linkTargets');
  const resolved = useResolvedTarget(storeId, kind, id);
  if (resolved.pending) return <span className="text-muted-foreground">{t('loading')}</span>;
  if (resolved.missing || !resolved.label) {
    return <span className="text-destructive">{t('missing')}</span>;
  }
  return (
    <span className="inline-flex min-w-0 flex-col">
      <span className="truncate">{resolved.label}</span>
      {showPath && resolved.detail ? (
        <span className="text-muted-foreground truncate font-mono text-xs">{resolved.detail}</span>
      ) : null}
    </span>
  );
}
